<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content modal-large">
      <div class="modal-header">
        <h3>{{ editingPost ? '✏️ ویرایش پست' : '➕ پست جدید' }}</h3>
        <button class="btn-close" @click="$emit('close')">✕</button>
      </div>

      <div class="modal-body">
        <!-- ========================================== -->
        <!-- نوع پست -->
        <!-- ========================================== -->
        <div class="form-group">
          <label class="required">نوع پست</label>
          <div class="type-selector">
            <button
                v-for="type in postTypes"
                :key="type.value"
                :class="['type-btn', { active: form.type === type.value }]"
                @click="selectType(type.value)"
            >
              <span class="type-icon">{{ type.icon }}</span>
              <span class="type-label">{{ type.label }}</span>
              <span class="type-desc">{{ type.desc }}</span>
            </button>
          </div>
        </div>

        <!-- ========================================== -->
        <!-- کپشن -->
        <!-- ========================================== -->
        <div class="form-group">
          <label>کپشن (اختیاری)</label>
          <textarea
              v-model="form.caption"
              class="form-textarea"
              rows="4"
              maxlength="2000"
              placeholder="توضیحی درباره این نمونه کار..."
          ></textarea>
          <small class="help-text">{{ form.caption.length }} / ۲۰۰۰</small>
        </div>

        <!-- ========================================== -->
        <!-- دسته‌بندی -->
        <!-- ========================================== -->
        <div class="form-group">
          <label>دسته‌بندی (اختیاری)</label>
          <div class="checkbox-group">
            <label v-for="line in lines" :key="line.id" class="checkbox-label">
              <input type="checkbox" :value="line.id" v-model="form.line_ids" />
              {{ line.title }}
            </label>
          </div>
        </div>

        <!-- ========================================== -->
        <!-- قبل و بعد: دو ImageSelector جداگانه -->
        <!-- ========================================== -->
        <template v-if="form.type === 'before_after'">
          <div class="image-row">
            <div class="form-group half">
              <label class="required">عکس قبل</label>
              <image-selector
                  label="عکس قبل"
                  :media_id.sync="beforeMediaId"
                  :preview-image="getBeforePreview()"
                  default_archive="post"
                  :multiple="false"
                  :crop-scale="'4:5'"
                  :crop-max-width="1080"
                  :crop-max-height="1350"
                  :convert-to-webp="true"
              />
            </div>
            <div class="form-group half">
              <label class="required">عکس بعد</label>
              <image-selector
                  label="عکس بعد"
                  :media_id.sync="afterMediaId"
                  :preview-image="getAfterPreview()"
                  default_archive="post"
                  :multiple="false"
                  :crop-scale="'4:5'"
                  :crop-max-width="1080"
                  :crop-max-height="1350"
                  :convert-to-webp="true"
              />
            </div>
          </div>
          <small class="help-text">اول عکس «قبل» و سپس عکس «بعد» را انتخاب کنید.</small>
        </template>

        <!-- ========================================== -->
        <!-- تکی -->
        <!-- ========================================== -->
        <template v-else-if="form.type === 'single'">
          <div class="form-group">
            <label class="required">عکس</label>
            <image-selector
                label="عکس پست"
                :media_id.sync="form.media_ids"
                :preview-image="getSinglePreview()"
                default_archive="post"
                :multiple="false"
                :crop-scale="'4:5'"
                :crop-max-width="1080"
                :crop-max-height="1350"
                :convert-to-webp="true"
            />
            <small class="help-text">دقیقاً ۱ عکس انتخاب کنید.</small>
          </div>
        </template>

        <!-- ========================================== -->
        <!-- اسلایدی -->
        <!-- ========================================== -->
        <template v-else-if="form.type === 'slide'">
          <div class="form-group">
            <label class="required">عکس‌های اسلاید</label>
            <image-selector
                label="عکس‌های اسلاید"
                :media_id.sync="form.media_ids"
                :preview-image="getSlidePreview()"
                default_archive="post"
                :multiple="true"
                :crop-scale="'4:5'"
                :crop-max-width="1080"
                :crop-max-height="1350"
                :convert-to-webp="true"
            />
            <small class="help-text">حداقل ۲ عکس انتخاب کنید. ترتیب انتخابی، ترتیب نمایش است.</small>
          </div>
        </template>
      </div>

      <div class="modal-footer">
        <button class="btn-secondary" @click="$emit('close')">انصراف</button>
        <button class="btn-primary" :disabled="submitting || !canSubmit" @click="submitForm">
          {{ submitting ? '⏳ در حال ذخیره...' : (editingPost ? '💾 ذخیره تغییرات' : '💾 ایجاد پست') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ImageSelector from '../includes/ImageSelector.vue';

export default {
  name: 'PostFormModal',

  components: {
    ImageSelector
  },

  props: {
    editingPost: {
      type: Object,
      default: null
    },
    lines: {
      type: Array,
      default: function() { return []; }
    }
  },

  data: function() {
    return {
      submitting: false,
      beforeMediaId: [],
      afterMediaId: [],
      form: {
        type: 'single',
        caption: '',
        line_ids: [],
        media_ids: []
      },
      postTypes: [
        { value: 'single', label: 'عکس تکی', icon: '🖼', desc: 'یک عکس' },
        { value: 'before_after', label: 'قبل و بعد', icon: '↔️', desc: 'دو عکس' },
        { value: 'slide', label: 'اسلایدی', icon: '🎠', desc: 'چند عکس' }
      ]
    };
  },

  computed: {
    canSubmit: function() {
      if (!this.form.type) return false;

      if (this.form.type === 'single') {
        return this.form.media_ids.length === 1;
      }

      if (this.form.type === 'before_after') {
        return this.beforeMediaId.length === 1 && this.afterMediaId.length === 1;
      }

      if (this.form.type === 'slide') {
        return this.form.media_ids.length >= 2;
      }

      return false;
    }
  },

  mounted: function() {
    if (this.editingPost) {
      this.form = {
        type: this.editingPost.type,
        caption: this.editingPost.caption || '',
        line_ids: this.editingPost.lines.map(function(l) { return l.id; }),
        media_ids: []
      };

      if (this.editingPost.type === 'before_after') {
        var sortedMedia = this.editingPost.media.slice().sort(function(a, b) {
          return a.order - b.order;
        });

        this.beforeMediaId = sortedMedia[0] ? [sortedMedia[0].media_id] : [];
        this.afterMediaId = sortedMedia[1] ? [sortedMedia[1].media_id] : [];

      } else {
        this.form.media_ids = this.editingPost.media.map(function(m) {
          return m.media_id;
        });
      }
    }
  },

  methods: {
    selectType: function(type) {
      this.form.type = type;
    },

    // ==========================================
    // پیش‌نمایش عکس‌ها
    // ==========================================
    getBeforePreview: function() {
      if (!this.editingPost || !this.editingPost.media) return null;
      var sorted = this.editingPost.media.slice().sort(function(a, b) {
        return a.order - b.order;
      });
      return sorted[0] ? sorted[0].url : null;
    },

    getAfterPreview: function() {
      if (!this.editingPost || !this.editingPost.media) return null;
      var sorted = this.editingPost.media.slice().sort(function(a, b) {
        return a.order - b.order;
      });
      return sorted[1] ? sorted[1].url : null;
    },

    getSinglePreview: function() {
      if (!this.editingPost || !this.editingPost.media) return null;
      return this.editingPost.media[0] ? this.editingPost.media[0].url : null;
    },

    getSlidePreview: function() {
      if (!this.editingPost || !this.editingPost.media) return null;
      return this.editingPost.media[0] ? this.editingPost.media[0].url : null;
    },

    // ==========================================
    // ارسال فرم
    // ==========================================
    submitForm: async function() {
      if (!this.canSubmit) return;

      this.submitting = true;
      try {
        var mediaIds = [];

        if (this.form.type === 'before_after') {
          mediaIds = [
            this.beforeMediaId[0],
            this.afterMediaId[0]
          ];
        } else {
          mediaIds = this.form.media_ids;
        }

        var payload = {
          type: this.form.type,
          caption: this.form.caption || null,
          line_ids: this.form.line_ids,
          media_ids: mediaIds
        };

        if (this.editingPost) {
          await axios.put('/api/admin/posts/' + this.editingPost.id, payload);
          this.showSuccess('پست ویرایش شد.');
        } else {
          await axios.post('/api/admin/posts', payload);
          this.showSuccess('پست ایجاد شد.');
        }

        this.$emit('success');

      } catch (error) {
        var msg = 'خطا در ذخیره پست';
        if (error.response && error.response.data && error.response.data.message) {
          msg = error.response.data.message;
        }
        this.showError(msg);
      } finally {
        this.submitting = false;
      }
    },

    showSuccess: function(msg) {
      if (this.$toast) this.$toast.success(msg);
      else alert(msg);
    },

    showError: function(msg) {
      if (this.$toast) this.$toast.error(msg);
      else alert(msg);
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-large {
  max-width: 700px;
  width: 100%;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #909399;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.form-group {
  margin-bottom: 20px;
}

.form-group.half {
  flex: 1;
  min-width: 0;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #2c3e50;
  margin-bottom: 8px;
}

.form-group label.required::after {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.image-row {
  display: flex;
  gap: 16px;
}

.type-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.type-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 14px 10px;
  background: #f5f7fa;
  border: 2px solid #e4e7ed;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s;
}

.type-btn:hover {
  border-color: #409eff;
}

.type-btn.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.type-icon { font-size: 24px; }
.type-label { font-size: 13px; font-weight: 700; color: #2c3e50; }
.type-desc { font-size: 11px; color: #909399; }

.form-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  box-sizing: border-box;
  font-family: inherit;
}

.form-textarea:focus {
  outline: none;
  border-color: #409eff;
}

.help-text {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: #f5f7fa;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: normal;
}

.btn-primary {
  padding: 10px 24px;
  background: #409eff;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn-primary:hover:not(:disabled) { background: #66b1ff; }
.btn-primary:disabled { background: #a0cfff; cursor: not-allowed; }

.btn-secondary {
  padding: 10px 24px;
  background: #909399;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
}

@media (max-width: 768px) {
  .type-selector { grid-template-columns: 1fr; }
  .image-row { flex-direction: column; gap: 0; }
}
</style>